import env from '@beam-australia/react-env'

// Required values from environment variables
const CLOUDWATCH_CONFIGURATION = {
  ENABLE: Number(env('CLOUDWATCH_RUM_ENABLE')),
  GUEST_ROLE_ARN: env('CLOUDWATCH_RUM_GUEST_ROLE_ARN'),
  IDENTITY_POOL_ID: env('CLOUDWATCH_RUM_IDENTITY_POOL_ID'),
  ENDPOINT: env('CLOUDWATCH_RUM_ENDPOINT'),
  APPLICATION_ID: env('CLOUDWATCH_RUM_APPLICATION_ID'),
}

// Validator functions
const hasCloudwatchVariables = () => Object.values(CLOUDWATCH_CONFIGURATION).every(Boolean)
const isClient = () => typeof window !== 'undefined'

// AwsRum client singleton
let awsRum

export async function initializeCloudwatch(): Promise<void> {
  // Should only initialize on client side and if we have all needed variables
  if (awsRum || !isClient() || !hasCloudwatchVariables()) {
    return
  }

  // Load it here to avoid adding this dependency to the main chunk
  const {AwsRum} = await import('aws-rum-web')

  // Define values for cloudwatch configuration, these aren't required to come from environment.
  const telemetries = env('CLOUDWATCH_RUM_TELEMETRIES')?.split(',') ?? [
    'performance',
    'errors',
    'http',
  ]

  const sessionSampleRate =
    typeof env('CLOUDWATCH_RUM_SESSION_SAMPLE_RATE') !== 'undefined'
      ? Number(env('CLOUDWATCH_RUM_SESSION_SAMPLE_RATE'))
      : 1
  const allowCookies =
    typeof env('CLOUDWATCH_RUM_ALLOW_COOKIES') !== 'undefined'
      ? Boolean(Number(env('CLOUDWATCH_RUM_ALLOW_COOKIES')))
      : true
  const enableXRay =
    typeof env('CLOUDWATCH_RUM_ENABLE_X_RAY') !== 'undefined'
      ? Boolean(Number(env('CLOUDWATCH_RUM_ENABLE_X_RAY')))
      : false

  const applicationVersion = env('CLOUDWATCH_RUM_APPLICATION_VERSION') || '1.0.0'
  const applicationRegion = env('CLOUDWATCH_RUM_APPLICATION_REGION') || 'us-east-1'

  const config = {
    guestRoleArn: CLOUDWATCH_CONFIGURATION.GUEST_ROLE_ARN,
    identityPoolId: CLOUDWATCH_CONFIGURATION.IDENTITY_POOL_ID,
    endpoint: CLOUDWATCH_CONFIGURATION.ENDPOINT,
    telemetries,
    sessionSampleRate,
    allowCookies,
    enableXRay,
  }

  // Try to initialize the client
  try {
    awsRum = new AwsRum(
      CLOUDWATCH_CONFIGURATION.APPLICATION_ID,
      applicationVersion,
      applicationRegion,
      config,
    )
  } catch (error) {
    console.error(error)
  }
}
